import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import axios from "axios"

/* --------------------------------- Program Listing ------------------------------ */

export interface IciciCbdcLocation {
  city: string
  state: string
  bufferZone: string
}

export interface BankDetails {
  mcc?: string | string[]
  payerVa?: string
  purposeCode?: string
  mandateType?: string
  accessKey?: string
  payeeVpa?: string
  // YES Bank specific
  clientIdentifier?: string
  mandateName?: string
  amountRule?: "MAX" | "EXACT" | string
  voucherCategory?: "CORPORATE" | "GOVT" | string
  recurrencePattern?: "ONETIME" | "ASPRESENTED" | "RELOADABLE" | string
  payerRevocable?: string
  preAuth?: boolean
  refurl?: string
  // ICICI CBDC specific fields - stored as plain bank details, same as any
  // other processor's bankDetails. Program creation/update no longer calls
  // any ICICI CBDC bank API, so there is no ruleId/authBy/expiry here.
  sponsorId?: string
  sponsorName?: string
  purpose?: string
  language?: "" | "ENGLISH" | "HINDI"
  location?: IciciCbdcLocation[]
  // MYP (Malaysia DuitNow QR) specific fields. See
  // programs/src/services/program.service.js#provisionMypProgram, which
  // reads these to build MYP's POST /programs request. MYP is always sent
  // activationMode: DIRECT server-side - VMS's own issuanceType (direct/
  // indirect) is the only lifecycle switch, so there's no field for it here.
  programType?: "LONG_RUNNING" | "OCCASION"
  expiry?: { mode: "DAYS_FROM_LOAD" | "FIXED_DATE"; days?: number | string; date?: string }
  limits?: {
    minLoad?: string
    maxLoad?: string
    maxBalance?: string
    perTransaction?: string
    dailySpend?: string
  }
  merchantRules?: { mode: "ALL" | "RESTRICTED"; mccs?: string; merchantIds?: string }
  lowFloatThreshold?: string
  // Written by the backend at provisioning time (program.service.js#provisionMypProgram) -
  // never edited directly, only carried forward on save so an edit doesn't wipe the program's MYP linkage.
  mypProgramId?: string
  mypProvisionStatus?: "provisioned" | "failed"
  mypProvisionError?: string
}

export interface Program {
  programId: string
  programName: string
  programCode: string
  description: string
  brandImage: string
  logo: string
  displayName: string
  standardTerms: string
  specificTerms: string
  voucherImage: string
  currency: string
  processorType: string
  processorTypeDisplay?: string
  issuanceType?: 'direct' | 'indirect'
  tagline: string
  cardDescription: string
  receiverInstructions: string
  receiverHowToUseInstructions: string
  callToAction: string
  brandImageAlt: string
  maximumAmount: number
  minimumAmount: number
  bankDetails: BankDetails
  redemptionType: string
  merchantId: string
  subMerchantId: string
  period: number
  status: string
  denominationAmount: number[],
  denominationCurrency: string,
  denominationMaxAmount: number,
  denominationMinAmount: number,
  denominationType: string,
  issuerBankId: string|null,
  issuerBank: {
    name: string,
    principalBankId: string
  },
  ProgramDistributorWallet: {
    contractStartDate: string;
    contractEndDate: string;
    discountRanges: {
      min_order_value: number;
      max_order_value: number | null;
      discount: number;
    }[];
    cumulativeBalance: string;
    cumulativeFaceValue: string;
    cumulativeTopUp: string;  
  },
  "processor.processorType": string,
  "processor.processorId": number,
  wallets: {
    principalWalletId: number;
    name: string;
    description: string;
    currency: string;
    balance: string;
    status: 'active' | 'inactive' | 'suspended';
    createdAt: string;
    updatedAt: string;
  }[]
}

export const fetchProgramList = createAsyncThunk(
  "program/fetchProgramList",
  async (
    {
      principalId = null,
      limit = 100,
    }: { principalId?: number | string | null; limit?: number },
    { rejectWithValue },
  ) => {
    const token = localStorage.getItem("accessToken")

    let params: any = { limit }
    if (principalId) {
      params["principalId"] = principalId
    }
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/programs/`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
        params,
      })

      return response.data["rows"]
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch programs",
      )
    }
  },
)

export const fetchProgramById = createAsyncThunk(
  "program/fetchProgramById",
  async (programId: number, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({    
        baseURL: `${window.env.API_URL}/v1/programs/programs/${programId}`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch program",
      )
    }
  },
)

/* --------------------------------- Create Program ------------------------------ */
export interface CreateProgramPayload {
  principalId: number
  programName: string
  programCode: string
  description: string
  standardTerms: string
  specificTerms: string
  displayName: string
  logo?: File | null | string
  brandImage?: File | null | string
  voucherImage?: File | null | string
  currency: string
  processorType: string
  processorTypeDisplay: string
  // Voucher issuance lifecycle for brands that fork on it (ICICI, YesBank,
  // ICICI CBDC) - '' when not yet chosen, or the brand has no lifecycle
  // fork (SELP). No 'pilot' value: pilot behaved identically to indirect
  // at the processor level.
  issuanceType: 'direct' | 'indirect' | ''
  tagline: string
  cardDescription: string
  receiverInstructions: string
  receiverHowToUseInstructions: string
  callToAction: string
  brandImageAlt: string
  maximumAmount: number
  minimumAmount: number
  issuerBankId: string|null
  redemptionType: string
  merchantId: string
  subMerchantId: string
  period: number
  bankDetails: BankDetails,
  denominationAmount: number[],
  denominationCurrency: string,
  denominationMaxAmount: number,
  denominationMinAmount: number,
  denominationType: string,
}


export const createProgram = createAsyncThunk(
  "program/createPorgram",
  async (formData: FormData, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/programs/`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "POST",
        data: formData,
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create program"
      )
    }
  },
)

/* --------------------------------- Update Program ------------------------------ */

export const updateProgram = createAsyncThunk(
  "program/updateProgram",
  async ({ formData, programId }: { formData: FormData; programId: string }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/programs/${programId}`,
        timeout: 100000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "PUT",
        data: formData,
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update program"
      )
    }
  },
)

/**
 * Explicitly (re)triggers MYP provisioning for an already-created program
 * (POST /programs/{id}/myp-provision), without resubmitting the rest of the
 * form - used by the "Provision with MYP" button in the MYP Bank Details
 * section so a failed provisioning (e.g. principal not yet onboarded with
 * MYP) can be retried on its own once fixed.
 */
export const provisionMypForProgram = createAsyncThunk(
  "program/provisionMypForProgram",
  async (programId: string, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/programs/${programId}/myp-provision`,
        timeout: 30000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to provision program with MYP"
      )
    }
  },
)

/* --------------------------------- Update Program ------------------------------ */

export const updateProgramStatus = createAsyncThunk(
  "program/updateProgramStatus",
  async ({ status, programId }: { status: string; programId: string }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/programs/status`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "PUT",
        params: {
          status: status,
          programId: programId,
        },
      })

      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update program"
      )
    }
  },
)


/* --------------------------------- Bank Listing ------------------------------ */

export interface Bank {
  bankId: number
  name: string
  logo: string
  displayName: string
}

export const fetchBankList = createAsyncThunk(
  "program/fetchBankList",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/banks/`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      return response.data["banks"]
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch banks",
      )
    }
  },
)

/* --------------------------------- Processor Listing ------------------------------ */

export interface Processor {
  processorId: number
  userId: number
  name: string
  description: string
  processorType: string
}

export const fetchProcessorList = createAsyncThunk(
  "program/fetchProcessorList",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken")
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/processors`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      return response.data["processors"]
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch processors",
      )
    }
  },
)

/* --------------------------------- Main State ------------------------------ */

interface ProgramState {
  programLoading: boolean
  programError: any
  programList: Program[] | null
  selectedProgram: Program | null
  bankList: Bank[] | null
  processorList: Processor[] | null
  // Separate from programLoading/programError so the "Provision with MYP"
  // button's own state doesn't interfere with the surrounding form's.
  mypProvisionLoading: boolean
  mypProvisionError: string | null
}

const initialState: ProgramState = {
  programLoading: false,
  programError: null,
  programList: null,
  selectedProgram: null,
  bankList: null,
  processorList: null,
  mypProvisionLoading: false,
  mypProvisionError: null,
}

const ProgramSlice = createSlice({
  name: "distributor",
  initialState,
  reducers: {
    clearProgram(state) {
      state.programList = null
      state.programLoading = false
      state.programError = null
    },
  },
  extraReducers: builder => {
    builder

      // Program List
      .addCase(fetchProgramList.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(
        fetchProgramList.fulfilled,
        (state, action: PayloadAction<Program[]>) => {
          state.programLoading = false
          state.programList = action.payload
        },
      )
      .addCase(fetchProgramList.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload || "Failed to fetch programs"
      })

      // Create Program
      .addCase(createProgram.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(createProgram.fulfilled, (state, action: PayloadAction<Program>) => {
        state.programLoading = false
        state.programError = null
        if (state.programList) {
          state.programList = [...state.programList, action.payload]
        } else {
          state.programList = [action.payload]
        }
      })
      .addCase(createProgram.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload
      })

       // Update Program
       .addCase(updateProgram.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(updateProgram.fulfilled, (state, action) => {
        state.programLoading = false
        state.programError = null
        if (state.programList) {
          state.programList = state.programList.map(program => 
            program.programId === action.payload.programId ? action.payload : program
          )
        }
      })
      .addCase(updateProgram.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload
      })

      // Provision (or retry provisioning) a program with MYP
      .addCase(provisionMypForProgram.pending, state => {
        state.mypProvisionLoading = true
        state.mypProvisionError = null
      })
      .addCase(provisionMypForProgram.fulfilled, (state, action: PayloadAction<Program>) => {
        state.mypProvisionLoading = false
        state.mypProvisionError = null
        state.selectedProgram = action.payload
        if (state.programList) {
          state.programList = state.programList.map(program =>
            program.programId === action.payload.programId ? action.payload : program
          )
        }
      })
      .addCase(provisionMypForProgram.rejected, (state, action) => {
        state.mypProvisionLoading = false
        state.mypProvisionError = action.payload as string
      })

      // Fetch Program by ID
      .addCase(fetchProgramById.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(
        fetchProgramById.fulfilled,
        (state, action: PayloadAction<Program>) => {
          state.programLoading = false
          state.selectedProgram = action.payload
        },
      )
      .addCase(fetchProgramById.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload || "Failed to fetch program"
      })

      // Fetch Bank List
      .addCase(fetchBankList.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(
        fetchBankList.fulfilled,
        (state, action: PayloadAction<Bank[]>) => {
          state.programLoading = false
          state.bankList = action.payload
        },
      )
      .addCase(fetchBankList.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload || "Failed to fetch banks"
      })

      // Fetch Processor List
      .addCase(fetchProcessorList.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(
        fetchProcessorList.fulfilled,
        (state, action: PayloadAction<Processor[]>) => {
          state.programLoading = false
          state.processorList = action.payload
        },
      )
      .addCase(fetchProcessorList.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload || "Failed to fetch processors"
      })

      // Update Program Status
      .addCase(updateProgramStatus.pending, state => {
        state.programLoading = true
        state.programError = null
      })
      .addCase(updateProgramStatus.rejected, (state, action) => {
        state.programLoading = false
        state.programError = action.payload
      })
      
      
  },
})

export const { clearProgram } = ProgramSlice.actions

export default ProgramSlice.reducer
